import { ViewMode } from '@efdigital/ll-addon-codemirror';
import type { Quiz } from '@efdigital/ll-addon-data';
import { selectValue } from '@efdigital/ll-addon-ui/modifiers/select-value';
import { MarkdownEditorWithImagePicker } from '@efdigital/ll-admin-v2/components/markdown-editor-with-image-picker';
import { SidebarAll } from '@efdigital/ll-admin-v2/lib/markdown-toolbar';
import { on } from '@ember/modifier';
import Component from '@glimmer/component';

import { type EditRecordSignature } from '../edit-record-base.gts';
import { EditRecordCollapse } from '../edit-record-collapse.gts';

enum QuizOutputTemplate {
  Default = 'default',
  Certificate = 'certificate',
}

const OUTPUT_TEMPLATES = Object.entries(QuizOutputTemplate).map(
  ([key, value]) => ({
    key,
    value,
  })
);

export class EditQuizOutput extends Component<EditRecordSignature<Quiz>> {
  setMarkdownOutput = (markdown: string) => {
    this.args.item.markdownOutput = markdown;
  };

  setTemplateOutput = (event: Event) => {
    const target = event.target as HTMLSelectElement;
    this.args.item.templateOutput = target.value;
  };

  get needsApproval(): boolean {
    return this.args.item.needsApproval ?? false;
  }

  toggleNeedsApproval = (event: Event) => {
    const target = event.target as HTMLInputElement;
    this.args.item.needsApproval = target.checked;
  };

  <template>
    <EditRecordCollapse
      @editLabel="Edit Results Presentation"
      @item={{@item}}
      @context={{@context}}
      @maxDepth={{1}}
      @collapseLabel="Results Presentation"
      ...attributes
    >
      <:help>
        <p class="form-text">
          Control the layout of the results page.
        </p>
      </:help>
      <:form as |uniqueId isDisabled isReadOnly|>
        <fieldset disabled={{isDisabled}} class="mb-0">
          <div class="form-group mb-3">
            <label for="{{uniqueId}}-template">Layout</label>
            <select
              class="form-control"
              id="{{uniqueId}}-template"
              {{selectValue @item.templateOutput}}
              {{on "change" this.setTemplateOutput}}
            >
              {{#each OUTPUT_TEMPLATES as |template|}}
                <option value={{template.value}}>{{template.key}}
                  ([{{template.value}}])</option>
              {{/each}}
            </select>
          </div>

          <div class="form-label">Introduction</div>
          <p class="form-text">Content displayed to users when they are viewing
            their results. If left blank, the text from the input form will be
            displayed.</p>
          {{#if isReadOnly}}
            <textarea
              class="form-control font-monospace form-control-small lh-sm mb-3"
              id="{{uniqueId}}-markdownOutput"
              rows="15"
              readonly
            >{{@item.markdownOutput}}</textarea>
          {{else}}
            <div class="mb-3">
              <MarkdownEditorWithImagePicker
                @markdown={{@item.markdownOutput}}
                @toolbar={{SidebarAll}}
                @mode={{ViewMode.EditorOnly}}
                @onUpdate={{this.setMarkdownOutput}}
                @requiredSize="email-image"
              >
                <:preview as |markdown|>
                  <pre>{{markdown}}</pre>
                </:preview>
              </MarkdownEditorWithImagePicker>
            </div>
          {{/if}}

          <div class="form-check mb-3">
            <input
              type="checkbox"
              class="form-check-input"
              id="{{uniqueId}}-needs-approval"
              checked={{this.needsApproval}}
              {{on "change" this.toggleNeedsApproval}}
            />
            <label class="form-check-label" for="{{uniqueId}}-needs-approval">
              Require manager approval
            </label>
            <div class="form-text">
              A manager must review and approve answers before results are
              shared.
            </div>
          </div>
        </fieldset>
      </:form>
    </EditRecordCollapse>
  </template>
}
